import * as signalR from '@microsoft/signalr';
import Config from './Config';
/**
 * 通讯客户端
 */
export default class SignalRClient {
    private static instance: SignalRClient;
    private connection: signalR.HubConnection;
    private startPromise: Promise<void> | null = null;
    /** 重连成功后的回调（页面可用它做一次兜底数据刷新） */
    private reconnectListeners: Set<() => void> = new Set<() => void>();
    /** onclose 之后的自动重连定时器 */
    private reconnectTimer: any = null;
    private reconnectDelay: number = 2000;

    public static get Instance() : SignalRClient {
        if (this.instance == null) {
            this.instance = new SignalRClient();
        }
        return this.instance;
    }
    
    constructor() {
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(Config.hubUrl)
            .withAutomaticReconnect() //自动重连
            .build();
        this.connection.onreconnected(() => {
            console.log('重连成功!');
            // 重连成功后重置 startPromise，确保后续调用不会用旧的 rejected promise
            this.startPromise = Promise.resolve();
            this.NotifyReconnected();
        });
        this.connection.onreconnecting(() => {
            console.log('重连中!');
        });
        this.connection.onclose(async () => {
            console.log("连接断开!")
            // 断连后重置，下次 invoke 会重新尝试连接
            this.startPromise = null;
            // withAutomaticReconnect 默认只重试 4 次，之后 onclose 就彻底停了；
            // 这里补一个无限重连，否则页面会一直停留在断线前最后一次推送的状态
            // （表现为设备已离线，但界面仍显示"设备在线"）
            this.ScheduleReconnect();
        });
    }

    /**
     * 添加重连成功回调
     */
    public AddReconnectListener(onBackFun: () => void) {
        this.reconnectListeners.add(onBackFun);
    }

    /**
     * 删除重连成功回调
     */
    public RemoveReconnectListener(onBackFun: () => void) {
        this.reconnectListeners.delete(onBackFun);
    }

    private NotifyReconnected() {
        this.reconnectDelay = 2000;
        this.reconnectListeners.forEach((fn) => {
            try {
                fn();
            } catch (e) {
                console.error('重连回调执行失败', e);
            }
        });
    }

    private ScheduleReconnect() {
        if (this.reconnectTimer) return;
        const delay = this.reconnectDelay;
        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            try {
                await this.StartAsync();
                console.log('断开后自动重连成功!');
                this.NotifyReconnected();
            } catch (e) {
                // 指数退避，最长 30 秒重试一次
                this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
                this.ScheduleReconnect();
            }
        }, delay);
    }

    /**
     * 添加监听
     */
    public AddEventListener(key: string, onBackFun: (...args: any[]) => void) {
        this.connection.on(key, onBackFun);
    }

    /**
     * 删除监听
     * @param key 
     */
    public RemoveEventListener(key: string) {
        this.connection.off(key);
    }

    /**
     * 启动服务，最多等待 10 秒
     */
    public async StartAsync() {
        if (this.startPromise) return this.startPromise;
        const promise = Promise.race([
            this.connection.start(),
            new Promise<void>((_, reject) => setTimeout(() => reject(new Error('SignalR 连接超时')), 10000)),
        ]);
        this.startPromise = promise;
        try {
            await promise;
        } catch (e) {
            // 失败后必须清空，否则后续重连会一直拿到同一个 rejected promise
            if (this.startPromise === promise) this.startPromise = null;
            throw e;
        }
    }

    /**
     * 确保连接已就绪（自动启动）
     */
    private async ensureConnected() {
        if (!this.startPromise) {
            await this.StartAsync();
        } else {
            await this.startPromise;
        }
    }

    /**
     * 发送数据并返回
     * @param args 
     * @returns 
     */
    public async InvokeAsync<T>(action: string, ...args: any[]) {
        await this.ensureConnected();
        return await this.connection.invoke(action, ...args) as T;
    }

    /**
     * 发送数据不返回
     * @param action 
     * @param args 
     */
    public async SendAsync(action: string, ...args: any[]) {
        await this.ensureConnected();
        await this.connection.send(action, ...args)
    }
}
